import React, { ReactNode, useState } from 'react';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BugReportIcon from '@mui/icons-material/BugReport';
import HelpOutlineIcon from '@mui/icons-material/Help';
import KeyIcon from '@mui/icons-material/Key';
import LogoutIcon from '@mui/icons-material/Logout';
import RateReviewIcon from '@mui/icons-material/RateReview';
import WebhookIcon from '@mui/icons-material/Webhook';
import { Divider, IconButton, Link, Menu, MenuItem, Paper, SxProps, Theme } from '@mui/material';

import {
  formatSubscriptionDate,
  getClientUrls,
  hasApiSessionsPermission,
  hasSubscriptionManagerPermission,
  hasWebhooksPermission,
  logOut,
} from '@paragon/login-tools';

import { ApiKeysDialog } from './ApiKeysDialog';
import { ExternalWebhooksDialog } from './ExternalWebhooksDialog';
import { useAppWithThemeDispatch, useAppWithThemeSelector } from './hooks';
import { ReportContext, ReportProblemDialog } from './ReportProblemDialog';
import { ThemeToggleMenuItem } from './ThemeToggleMenuItem';

interface Props {
  readonly sx?: SxProps<Theme>;
  readonly hideThemeToggleButton?: boolean;
  readonly simpleUserDetail?: boolean;
  readonly additionalMenuItems?: (closeMenu: () => void) => ReactNode;
  readonly children?: ReactNode;

  /**
   * For the Paragon Services app, we want to open all pages in the same tab
   */
  readonly servicesPagesOpenInSameTab?: boolean;

  /**
   * Lets a client add to what a problem report says about where the user is. The report always carries
   * `window.location.href`, which is enough for most clients; Design adds the names behind that address, since its
   * URLs are all GUIDs.
   */
  readonly reportContext?: ReportContext;

  /**
   * Lets a client attach a picture of what the user is looking at, as a PNG data URL, or `null` when unavailable.
   *
   * Called only when the user opens the dialog — for the Design client this triggers a render of the 3D scene, so it
   * must not run on any other code path.
   */
  readonly getReportScreenshot?: () => string | null;
}
export function UserDropdown({
  sx = [],
  hideThemeToggleButton,
  additionalMenuItems,
  children,
  servicesPagesOpenInSameTab,
  reportContext,
  getReportScreenshot,
}: Props) {
  const dispatch = useAppWithThemeDispatch();
  const [anchorEl, setAnchorEl] = useState<any | null>(null);
  const urls = useAppWithThemeSelector(getClientUrls);

  const user = useAppWithThemeSelector((state) => state.logInData.loggedInUser);
  const showUserDetails = user && (user.firstName || user.lastName) && user.email;
  const ongoingFreeTrial = useAppWithThemeSelector((state) => state.logInData.subscription === 'trial');
  const cancelAtPeriodEnd = useAppWithThemeSelector((state) => state.logInData.subscriptionCancelsAtPeriodEnd);
  const currentPeriodEnd = useAppWithThemeSelector((state) => state.logInData.subscriptionCurrentPeriodEnd);

  const isSubscriptionManager = useAppWithThemeSelector(hasSubscriptionManagerPermission);
  const canCreateApiSessions = useAppWithThemeSelector(hasApiSessionsPermission);
  const canManageWebhooks = useAppWithThemeSelector(hasWebhooksPermission);

  const [showApiKeysDialog, setShowApiKeysDialog] = useState(false);
  const [showWebhooksDialog, setShowWebhooksDialog] = useState(false);
  const [showReportProblemDialog, setShowReportProblemDialog] = useState(false);

  const handleClick = (event: React.MouseEvent<EventTarget>) => {
    setAnchorEl(event.target as any);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  let accountInfo: string | null = null;
  if (ongoingFreeTrial) {
    accountInfo = `Trial ends ${formatSubscriptionDate(user!.trialEndDate!)}`;
  } else if (
    currentPeriodEnd &&
    // If cancelling/renewing within 15 days, show the date
    new Date().getTime() > new Date(currentPeriodEnd).setDate(new Date(currentPeriodEnd).getDate() - 15)
  ) {
    accountInfo = cancelAtPeriodEnd
      ? `Cancels ${formatSubscriptionDate(currentPeriodEnd)}`
      : `Renews ${formatSubscriptionDate(currentPeriodEnd)}`;
  }

  return user != null ? (
    <>
      <IconButton onClick={handleClick} aria-haspopup="true" sx={sx}>
        <AccountCircleIcon sx={{ color: '#ffffff' }} />
      </IconButton>

      {showApiKeysDialog && <ApiKeysDialog onClose={() => void setShowApiKeysDialog(false)} />}
      {showWebhooksDialog && <ExternalWebhooksDialog onClose={() => void setShowWebhooksDialog(false)} />}
      {showReportProblemDialog && (
        <ReportProblemDialog
          onClose={() => void setShowReportProblemDialog(false)}
          reportContext={reportContext}
          getReportScreenshot={getReportScreenshot}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleClose}>
          {showUserDetails && `${user.firstName} ${user.lastName}`}
          {showUserDetails && <br />}
          {user?.organization ?? 'Organization'}
        </MenuItem>
        <Divider />
        <MenuItem
          component={Link}
          underline="none"
          color="inherit"
          target="_blank"
          rel="noreferrer noopener"
          href="https://docs.paragontruss.com"
        >
          <HelpOutlineIcon sx={{ mr: 1 }} />
          Documentation
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setShowReportProblemDialog(true);
          }}
        >
          <BugReportIcon sx={{ mr: 1 }} />
          Report a Problem
        </MenuItem>

        <MenuItem
          component={Link}
          underline="none"
          color="inherit"
          target="_blank"
          rel="noreferrer noopener"
          href="https://paragontruss.com/feedback"
        >
          <RateReviewIcon sx={{ mr: 1 }} />
          Satisfaction Survey
        </MenuItem>

        {canCreateApiSessions && (
          <MenuItem
            onClick={() => {
              handleClose();
              setShowApiKeysDialog(true);
            }}
          >
            <KeyIcon sx={{ mr: 1 }} />
            API Keys
          </MenuItem>
        )}

        {canManageWebhooks && (
          <MenuItem
            onClick={() => {
              handleClose();
              setShowWebhooksDialog(true);
            }}
          >
            <WebhookIcon sx={{ mr: 1 }} />
            Webhooks
          </MenuItem>
        )}

        {isSubscriptionManager && (
          <MenuItem
            component={Link}
            underline="none"
            color="inherit"
            target={servicesPagesOpenInSameTab ? '_self' : '_blank'}
            rel="noreferrer noopener"
            href={`${urls.servicesUrl}account`}
          >
            <AccountCircleIcon sx={{ mr: 1 }} />
            Account
            {accountInfo ? (
              <Paper elevation={6} sx={{ ml: 1, px: 1, backgroundColor: 'primary.main' }}>
                {accountInfo}
              </Paper>
            ) : null}
          </MenuItem>
        )}
        {hideThemeToggleButton ? null : <ThemeToggleMenuItem />}
        {additionalMenuItems ? additionalMenuItems(handleClose) : null}
        <MenuItem onClick={() => dispatch(logOut())}>
          <LogoutIcon sx={{ mr: 1 }} />
          Log Out
        </MenuItem>
        {children}
      </Menu>
    </>
  ) : null;
}
